import { TagDto } from '../types/admin.types';
import { Routes } from '../types/constants';
import { Video } from '../types/video.types';
import { parseSearchInput } from './video-search';

/**
 * Search filters to preserve when routing from results to a watch page.
 */
export interface SearchRouteContext {
    query?: string;
    tags?: string[];
}

/**
 * Builds a watch route, optionally preserving the search context that led to the video.
 */
export const buildVideoWatchRoute = (video: Video, context: SearchRouteContext = {}) => {
    const params = new URLSearchParams();
    params.set('v', video.Url);

    const query = context.query?.trim();
    if (query) {
        params.set('q', query);
    }

    context.tags
        ?.map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
        .forEach((tag) => params.append('tags', tag));

    return `${Routes.WATCH}?${params.toString()}`;
};

/**
 * Builds a video results route from a free-form search input with #tag parsing.
 */
export const buildVideoResultsRoute = (inputValue: string) => {
    const parsedInput = parseSearchInput(inputValue);
    return buildVideoResultsRouteFromParts(parsedInput.query, parsedInput.tags, inputValue);
};

/**
 * Builds a video results route from a text query and selected tag list.
 */
export const buildVideoResultsRouteFromParts = (
    query: string,
    tags: string[],
    fallbackQuery?: string
) => {
    const params = new URLSearchParams();
    const trimmedQuery = query.trim();

    if (trimmedQuery.length > 0) {
        params.set('q', trimmedQuery);
    }

    tags
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
        .forEach((tag) => params.append('tags', tag));

    if (params.toString().length === 0 && fallbackQuery?.trim()) {
        params.set('q', fallbackQuery.trim());
    }

    const routeQuery = params.toString();
    return routeQuery.length > 0 ? `${Routes.VIDEOS}?${routeQuery}` : Routes.VIDEOS;
};

/**
 * Builds a video results route scoped to a single tag.
 */
export const buildTagResultsRoute = (tag: Pick<TagDto, 'name'>) => (
    buildVideoResultsRouteFromParts('', [tag.name])
);

/**
 * Builds a video results route scoped to a single text value such as a fighter or event.
 */
export const buildTextResultsRoute = (value: string) => (
    buildVideoResultsRouteFromParts(value, [])
);
